import { useFeatureFlagController, useFeatureList } from '@/scopes/common/infrastructure/feature-flags';
import { Label, Switch, tk, Typography } from '@sunwebgroup/ui-components';
import { Command } from 'cmdk';
import { not, prop } from 'ramda';
import * as React from 'react';
import { useUpdateEffect } from 'react-use';
import useKeyboardJs from 'react-use/lib/useKeyboardJs';
import { styled } from 'styled-components';
import type { FeatureFlagId } from '@/scopes/common/infrastructure/feature-flags/featureFlags';

const Dialog = styled(Command.Dialog)`
  position: fixed;
  max-width: 50%;
  min-width: 600px;
  max-height: 80vh;
  top: 10vh;
  left: 50%;
  transform: translateX(-50%);
  overflow-y: auto;

  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_bottom')};
  border-radius: ${tk('radius_large')};

  z-index: 99999;
`;

const List = styled(Command.List)`
  padding: ${tk('spacing_xs')} ${tk('spacing_md')};
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  width: 100%;

  background: ${tk('color_semantic_bg')};
  padding: ${tk('spacing_md')};

  &:before {
    content: ' ';
    position: absolute;
    left: 0;
    bottom: 0;
    transform: translateY(100%);
    width: 100%;
    height: 18px;

    background: linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.7) 50%, transparent 100%);
  }
`;

const Badge = styled(Label)<{ $color: string }>`
  background: ${prop('$color')};
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${tk('spacing_2xs')};

  padding: ${tk('spacing_sm')} ${tk('spacing_md')};
  outline: none;
  cursor: pointer;
  box-sizing: border-box;

  border: 2px solid ${tk('color_semantic_border')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_input')};
  background: ${tk('color_semantic_bg')};

  height: 48px;
`;

const Input = styled(Command.Input)`
  width: 100%;
  border: none;
  outline: none;
  font-family: ${tk('typography_body_md_typeface')};
  line-height: ${tk('typography_body_md_line_height')};
  font-weight: ${tk('typography_body_md_weight')};
  letter-spacing: ${tk('typography_body_md_letter_spacing')};
`;

const Empty = styled(Command.Empty)`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${tk('spacing_md')};
`;

const GroupTitle = styled.div`
  padding: ${tk('spacing_xs')};
`;

const Group = styled(Command.Group)`
  margin-bottom: ${tk('spacing_sm')};
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${tk('spacing_xs')};
`;

const Item = styled(Command.Item)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  cursor: pointer;
  padding: ${tk('spacing_sm')} ${tk('spacing_md')};
  border-radius: ${tk('radius_medium')};
  margin-bottom: ${tk('spacing_2xs')};

  &:hover {
    background: ${tk('color_semantic_bg_subdued_hover')};
  }

  &[data-selected='true'] {
    background: ${tk('color_semantic_bg_subdued_hover')};
  }
`;

//const Separator = styled(Command.Separator)`
//  width: 100%;
//  border-bottom: 2px solid ${tk('color_semantic_border_subdued')};
//  margin-bottom: ${tk('spacing_2xs')};
//`;

function FeatureFlagItem({ id }: { id: FeatureFlagId }) {
  const { isActive, feature, toggleFeature } = useFeatureFlagController(id);

  return (
    <Item onSelect={toggleFeature}>
      <ItemContent>
        <Badge $color={feature?.color}>{feature.owner}</Badge>
        <Typography $variant="body-sm">{feature.name}</Typography>
      </ItemContent>
      <Switch checked={isActive} />
    </Item>
  );
}

function FeatureFlagsCommandGroup() {
  const featureFlags = useFeatureList();

  return (
    <Group
      heading={
        <GroupTitle>
          <Typography $variant="body-lg">Feature Flags</Typography>
        </GroupTitle>
      }
    >
      {featureFlags.map((id) => (
        <FeatureFlagItem key={id} id={id} />
      ))}
    </Group>
  );
}

export function DevCommands() {
  const [open, setOpen] = React.useState(false);
  const [isPressed] = useKeyboardJs('ctrl + k');

  useUpdateEffect(() => {
    if (isPressed) setOpen(not);
  }, [isPressed]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Header>
        <SearchBar>
          <CommandIcon />
          <Input />
        </SearchBar>
      </Header>
      <List>
        <Empty>
          <Typography>No results found.</Typography>
        </Empty>
        <FeatureFlagsCommandGroup />
        {/* <ExperimentsGroup /> */}
      </List>
    </Dialog>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Not used by now
function ExperimentsGroup() {
  return (
    <Group
      heading={
        <GroupTitle>
          <Typography $variant="body-lg">Experiments</Typography>
        </GroupTitle>
      }
    >
      <Item>
        <Typography $variant="body-sm">A experiment</Typography>
        <Switch />
      </Item>
      <Item>
        <Typography $variant="body-sm">Another experiment</Typography>
        <Switch />
      </Item>
    </Group>
  );
}

function CommandIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      className="lucide lucide-terminal"
      viewBox="0 0 24 24"
    >
      <path d="M4 17L10 11 4 5" />
      <path d="M12 19L20 19" />
    </svg>
  );
}
